import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Modal,
  Button,
  ProgressBar,
  Badge,
  Card,
  ListGroup,
  Form,
  Tab,
  Tabs,
  Spinner,
  Alert,
  Dropdown,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaTasks,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaSearch,
  FaPlus,
  FaChevronDown,
  FaUserCircle,
  FaEdit,
  FaTrash,
  FaSync,
} from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import { format, isValid } from "date-fns";
import axios from "axios";

// API endpoints
const TASK_API_BASE = "http://localhost:5890";
const DEVELOPER_API_URL =
  "http://localhost:5890/adminperson/developers";

// Helper functions
const getBadgeVariant = (priority) => {
  switch (priority) {
    case "High":
      return "danger";
    case "Medium":
      return "warning";
    case "Low":
      return "success";
    default:
      return "secondary";
  }
};

const getStatusVariant = (status) => {
  switch (status) {
    case "Almost Done":
      return "success";
    case "In Progress":
      return "primary";
    case "Not Started":
      return "secondary";
    default:
      return "info";
  }
};

const getBorderColor = (priority) => {
  switch (priority) {
    case "High":
      return "border-start border-4 border-danger";
    case "Medium":
      return "border-start border-4 border-warning";
    case "Low":
      return "border-start border-4 border-success";
    default:
      return "";
  }
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case "High":
      return <FaExclamationTriangle className="text-danger me-1" />;
    case "Medium":
      return <FaArrowUp className="text-warning me-1" />;
    case "Low":
      return <FaArrowDown className="text-success me-1" />;
    default:
      return null;
  }
};

// Safe date formatter
const formatDateSafe = (dateString) => {
  if (!dateString) return "No due date";

  try {
    const date = new Date(dateString);
    if (!isValid(date)) return "Invalid date";
    return format(date, "dd MMM yyyy");
  } catch (error) {
    console.error("Date formatting error:", error);
    return "Invalid date";
  }
};

const TaskTracker = () => {
  const [tasks, setTasks] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDeleteId, setTaskToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [developerLoading, setDeveloperLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isUpdatingTask, setIsUpdatingTask] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [newTask, setNewTask] = useState({
    task: "",
    priority: "Medium",
    assigned: "",
    developerId: "",
    dueDate: "",
    progress: 0,
    status: "Not Started",
    description: "",
  });
  const [editTask, setEditTask] = useState({
    id: "",
    task: "",
    priority: "Medium",
    assigned: "",
    developerId: "",
    dueDate: "",
    progress: 0,
    status: "Not Started",
    description: "",
  });
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [editSelectedDeveloper, setEditSelectedDeveloper] = useState(null);

  // Calculate priority counts
  const priorityCounts = {
    High: tasks.filter((task) => task.priority === "High").length,
    Medium: tasks.filter((task) => task.priority === "Medium").length,
    Low: tasks.filter((task) => task.priority === "Low").length,
  };

  // Fetch tasks from API
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${TASK_API_BASE}/tasks`);
      const formattedTasks = response.data.map((task) => ({
        ...task,
        _id: task._id,
        task: String(task.task || "Untitled Task"),
        assigned: String(task.assigned || "Unassigned"),
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString() : null,
        progress: Number(task.progress) || 0,
        description: String(task.description || ""),
      }));
      setTasks(formattedTasks);
    } catch (err) {
      setError(`Failed to fetch tasks: ${String(err.message)}`);
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Fetch developers from API
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const token = sessionStorage.getItem("token");
        setDeveloperLoading(true);
        const response = await axios.get(DEVELOPER_API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const formattedDevelopers = response.data.map((dev) => ({
          ...dev,
          name: dev.name || dev.developerName || "Unknown Developer",
          _id: dev._id,
        }));
        setDevelopers(formattedDevelopers);
      } catch (err) {
        setError(`Failed to fetch developers: ${String(err.message)}`);
        console.error("Error fetching developers:", err);
      } finally {
        setDeveloperLoading(false);
      }
    };

    fetchDevelopers();
  }, []);

  const handleShowTaskModal = (task) => {
    if (!task) return;
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
    setShowTaskModal(false);
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewTask({
      task: "",
      priority: "Medium",
      assigned: "",
      developerId: "",
      dueDate: "",
      progress: 0,
      status: "Not Started",
      description: "",
    });
    setSelectedDeveloper(null);
  };

  const handleShowEditModal = (task) => {
    if (!task) return;
    setEditTask({
      id: task._id,
      task: task.task,
      priority: task.priority,
      assigned: task.assigned,
      developerId: task.developerId,
      dueDate: task.dueDate,
      progress: task.progress,
      status: task.status,
      description: task.description,
    });

    const dev = developers.find((d) => d._id === task.developerId);
    setEditSelectedDeveloper(dev);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditTask({
      id: "",
      task: "",
      priority: "Medium",
      assigned: "",
      developerId: "",
      dueDate: "",
      progress: 0,
      status: "Not Started",
      description: "",
    });
    setEditSelectedDeveloper(null);
  };

  const handleShowDeleteModal = (taskId) => {
    setTaskToDeleteId(taskId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTaskToDeleteId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditTask((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeveloperSelect = (developer) => {
    if (!developer) return;
    setSelectedDeveloper(developer);
    setNewTask((prev) => ({
      ...prev,
      assigned: developer.name || "Unknown",
      developerId: developer._id,
    }));
  };

  const handleEditDeveloperSelect = (developer) => {
    if (!developer) return;
    setEditSelectedDeveloper(developer);
    setEditTask((prev) => ({
      ...prev,
      assigned: developer.name || "Unknown",
      developerId: developer._id,
    }));
  };

  const handleAddTask = async () => {
    try {
      setIsAddingTask(true);
      setError(null);

      if (!newTask.task || !newTask.developerId) {
        setError("Task name and a developer must be selected.");
        setIsAddingTask(false);
        return;
      }

      const taskData = {
        employee_ID: newTask.developerId,
        task: newTask.task,
        description: newTask.description,
        priority: newTask.priority,
        status: newTask.status,
        progress: parseInt(newTask.progress, 10) || 0,
        dueDate: newTask.dueDate || null,
        assigned: newTask.assigned,
      };

      await axios.post(`${TASK_API_BASE}/add-task`, taskData);

      handleCloseAddModal();
      fetchTasks(); // Reload tasks to see the new addition
    } catch (err) {
      setError(`Failed to add task: ${String(err.response?.data?.message || err.message || err)}`);
      console.error("Error adding task:", err.response?.data || err);
    } finally {
      setIsAddingTask(false);
    }
  };

  const handleUpdateTask = async () => {
    try {
      setIsUpdatingTask(true);
      setError(null);
      
      const taskData = {
        employee_ID: editTask.developerId,
        task: String(editTask.task),
        description: String(editTask.description),
        priority: editTask.priority,
        status: editTask.status,
        progress: parseInt(editTask.progress) || 0,
        dueDate: editTask.dueDate,
        assigned: String(editTask.assigned),
      };

      await axios.put(
        `${TASK_API_BASE}/edit-task/${editTask.id}`,
        taskData
      );

      handleCloseEditModal();
      handleCloseTaskModal();
      fetchTasks(); // Reload tasks to see the changes
    } catch (err) {
      setError(
        `Failed to update task: ${String(err.response?.data?.message || err.message || err)}`
      );
      console.error("Error updating task:", err.response?.data);
    } finally {
      setIsUpdatingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDeleteId) return;

    try {
      setIsDeletingTask(true);
      setError(null);
      await axios.delete(`${TASK_API_BASE}/delete-task/${taskToDeleteId}`);
      handleCloseDeleteModal();
      handleCloseEditModal();
      handleCloseTaskModal();
      fetchTasks(); // Reload tasks to see the deletion
    } catch (err) {
      setError(`Failed to delete task: ${String(err.message || err)}`);
      console.error("Error deleting task:", err);
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      setError(null);
      await axios.patch(`${TASK_API_BASE}/tasks/${taskId}`, {
        status: newStatus,
      });
      fetchTasks(); // Reload tasks to reflect the status change
      // Also update selectedTask state if it's the one being modified
      setSelectedTask((prev) => (prev && prev._id === taskId ? { ...prev, status: newStatus } : prev));
    } catch (err) {
      setError(`Failed to update status: ${String(err.message || err)}`);
      console.error("Error updating status:", err);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const taskName = String(task.task || "").toLowerCase();
    const assignedName = String(task.assigned || "").toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();

    return (
      taskName.includes(searchTermLower) || assignedName.includes(searchTermLower)
    );
  });

  const notStartedTasks = filteredTasks.filter(
    (task) => task.status === "Not Started"
  );
  const inProgressTasks = filteredTasks.filter(
    (task) => task.status === "In Progress"
  );
  const almostDoneTasks = filteredTasks.filter(
    (task) => task.status === "Almost Done"
  );

  const renderReloadButton = () => (
    <Button variant="outline-primary" onClick={fetchTasks} disabled={loading} className="d-flex align-items-center">
      <FaSync className={loading ? "spin me-2" : "me-2"} />
      {loading ? "Reloading..." : "Reload"}
    </Button>
  );

  return (
    <div className="container bg-white p-4 rounded-3 mt-4 shadow-sm border border-success">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-0 fw-bold text-success">
            <FaTasks className="me-2" />
            Task Tracker
          </h3>
          <p className="text-muted mb-0">Manage your team's tasks efficiently</p>
        </div>
        <div className="d-flex gap-2">
          <div className="input-group" style={{ width: "250px" }}>
            <span className="input-group-text bg-white">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            variant="outline-success"
            className="d-flex align-items-center me-2"
          >
            <FaFilter className="me-2" />
            Filter
          </Button>
          <Button
            variant="success"
            className="d-flex align-items-center"
            onClick={handleShowAddModal}
            disabled={isAddingTask}
          >
            {isAddingTask ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaPlus className="me-2" />
            )}
            {isAddingTask ? "Adding..." : "Add Task"}
          </Button>
          {renderReloadButton()}
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="border-danger shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-danger mb-1">High Priority</h6>
                  <h2 className="mb-0">{priorityCounts.High}</h2>
                  <small className="text-muted">Urgent tasks</small>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded">
                  <FaExclamationTriangle className="text-danger" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-warning shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-warning mb-1">Medium Priority</h6>
                  <h2 className="mb-0">{priorityCounts.Medium}</h2>
                  <small className="text-muted">Important tasks</small>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FaArrowUp className="text-warning" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-success shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-success mb-1">Low Priority</h6>
                  <h2 className="mb-0">{priorityCounts.Low}</h2>
                  <small className="text-muted">Normal tasks</small>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaArrowDown className="text-success" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
        fill
      >
        <Tab eventKey="all" title="All Tasks" className="border-0" />
        <Tab eventKey="notStarted" title="Not Started" className="border-0" />
        <Tab eventKey="inProgress" title="In Progress" className="border-0" />
        <Tab eventKey="almostDone" title="Almost Done" className="border-0" />
      </Tabs>

      {loading && !tasks.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="success" />
          <p className="mt-2">Loading tasks...</p>
        </div>
      ) : (
        <>
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Assigned</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === "all"
                  ? filteredTasks
                  : activeTab === "notStarted"
                  ? notStartedTasks
                  : activeTab === "inProgress"
                  ? inProgressTasks
                  : almostDoneTasks
                ).map((task) => (
                  <tr
                    key={task._id}
                    className={`${getBorderColor(task.priority)}`}
                  >
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                      <div className="fw-semibold text-dark">
                        {task.task || "Untitled Task"}
                      </div>
                      <small className="text-muted">
                        {task.description || "No description"}
                      </small>
                    </td>
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                      <Badge
                        pill
                        bg={getBadgeVariant(task.priority)}
                        className="d-flex align-items-center"
                      >
                        {getPriorityIcon(task.priority)}
                        {task.priority || "Medium"}
                      </Badge>
                    </td>
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }} className="d-flex align-items-center gap-2">
                      <div>
                        <div className="fw-medium">
                          {task.assigned || "Unassigned"}
                        </div>
                        <small className="text-muted">Developer</small>
                      </div>
                    </td>
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                      <div className="d-flex align-items-center">
                        <FaCalendarAlt className="me-2 text-secondary" />
                        {formatDateSafe(task.dueDate)}
                      </div>
                    </td>
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                      <Badge bg={getStatusVariant(task.status)}>
                        {task.status || "Not Started"}
                      </Badge>
                    </td>
                    <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                      <div className="d-flex align-items-center">
                        <ProgressBar
                          now={task.progress || 0}
                          className="flex-grow-1 me-2"
                          variant={
                            (task.progress || 0) > 80
                              ? "success"
                              : (task.progress || 0) > 50
                              ? "info"
                              : "warning"
                          }
                        />
                        <span className="fw-medium">
                          {task.progress || 0}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowEditModal(task);
                          }}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowDeleteModal(task._id);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-md-none">
            {(activeTab === "all"
              ? filteredTasks
              : activeTab === "notStarted"
              ? notStartedTasks
              : activeTab === "inProgress"
              ? inProgressTasks
              : almostDoneTasks
            ).map((task) => (
              <Card
                key={task._id}
                className={`mb-3 shadow-sm ${getBorderColor(task.priority)}`}
                onClick={() => handleShowTaskModal(task)}
                style={{ cursor: "pointer" }}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <Card.Title className="mb-1 text-success">
                        {task.task || "Untitled Task"}
                      </Card.Title>
                      <small className="text-muted">
                        {(task.description || "").substring(0, 60)}
                        {(task.description || "").length > 60 ? "..." : ""}
                      </small>
                    </div>
                    <Badge pill bg={getBadgeVariant(task.priority)}>
                      {task.priority || "Medium"}
                    </Badge>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div>
                      <div className="fw-medium">
                        {task.assigned || "Unassigned"}
                      </div>
                      <Badge bg={getStatusVariant(task.status)} size="sm">
                        {task.status || "Not Started"}
                      </Badge>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <FaCalendarAlt className="me-2 text-secondary" />
                      <small>Due: {formatDateSafe(task.dueDate)}</small>
                    </div>
                    <div className="text-end">
                      <small className="fw-medium">
                        {task.progress || 0}%
                      </small>
                    </div>
                  </div>
                  <ProgressBar
                    now={task.progress || 0}
                    variant={
                      (task.progress || 0) > 80
                        ? "success"
                        : (task.progress || 0) > 50
                        ? "info"
                        : "warning"
                    }
                  />
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowEditModal(task);
                      }}
                    >
                      <FaEdit /> Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowDeleteModal(task._id);
                      }}
                    >
                      <FaTrash /> Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}

      {selectedTask && (
        <Modal show={showTaskModal} onHide={handleCloseTaskModal} centered size="lg">
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold text-success">
              <FaTasks className="me-2" />
              Task Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h4 className="fw-bold mb-0">
                {selectedTask.task || "Untitled Task"}
              </h4>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTaskModal(false);
                  handleShowEditModal(selectedTask);
                }}
              >
                <FaEdit className="me-1" /> Edit Task
              </Button>
            </div>
            <div className="d-flex gap-3 mb-4">
              <Badge
                pill
                bg={getBadgeVariant(selectedTask.priority)}
                className="d-flex align-items-center"
              >
                {getPriorityIcon(selectedTask.priority)}
                {selectedTask.priority || "Medium"}
              </Badge>
              <Badge bg={getStatusVariant(selectedTask.status)}>
                {selectedTask.status || "Not Started"}
              </Badge>
            </div>
            <div className="row mb-4">
              <div className="col-md-6">
                <ListGroup variant="flush">
                  <ListGroup.Item className="border-0 px-0 py-2 d-flex align-items-center">
                    <div className="me-3">
                      <FaUserCircle size={20} className="text-muted" />
                    </div>
                    <div>
                      <strong>Assigned To:</strong>{" "}
                      {selectedTask.assigned || "Unassigned"}
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item className="border-0 px-0 py-2 d-flex align-items-center">
                    <div className="me-3">
                      <FaCalendarAlt className="text-muted" />
                    </div>
                    <div>
                      <strong>Due Date:</strong>{" "}
                      {formatDateSafe(selectedTask.dueDate)}
                    </div>
                  </ListGroup.Item>
                </ListGroup>
              </div>
              <div className="col-md-6">
                <div className="bg-light p-3 rounded">
                  <h6>Progress</h6>
                  <ProgressBar
                    now={selectedTask.progress || 0}
                    variant={
                      (selectedTask.progress || 0) > 80
                        ? "success"
                        : (selectedTask.progress || 0) > 50
                        ? "info"
                        : "warning"
                    }
                    className="mb-3"
                    animated
                  />
                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() =>
                        handleUpdateStatus(selectedTask._id, "Almost Done")
                      }
                      disabled={selectedTask.status === "Almost Done"}
                    >
                      <FaCheckCircle className="me-1" />
                      Mark as Complete
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        handleUpdateStatus(selectedTask._id, "In Progress")
                      }
                      disabled={selectedTask.status === "In Progress"}
                    >
                      Start Progress
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <h6>Description</h6>
              <div className="bg-light p-3 rounded">
                <p className="mb-0">
                  {selectedTask.description || "No description available"}
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseTaskModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Modal show={showAddModal} onHide={handleCloseAddModal} centered size="lg" className="task-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success">
            <FaPlus className="me-2" />
            Add New Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3">
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Task Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="task"
                    value={newTask.task}
                    onChange={handleInputChange}
                    placeholder="Enter task name"
                    required
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={newTask.description}
                    onChange={handleInputChange}
                    placeholder="Enter task description"
                    required
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={newTask.priority}
                    onChange={handleInputChange}
                    required
                    className="py-2 border-2"
                  >
                    <option value="High" className="text-danger">High</option>
                    <option value="Medium" className="text-warning">Medium</option>
                    <option value="Low" className="text-success">Low</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={newTask.status}
                    onChange={handleInputChange}
                    required
                    className="py-2 border-2"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Almost Done">Almost Done</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Progress (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="progress"
                    value={newTask.progress}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    placeholder="0-100"
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Assigned To</Form.Label>
                  <Dropdown onSelect={(eventKey) => handleDeveloperSelect(JSON.parse(eventKey))}>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="dropdown-assigned"
                      className="w-100 d-flex justify-content-between align-items-center"
                    >
                      {selectedDeveloper ? selectedDeveloper.name : "Select Developer"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {developerLoading ? (
                        <Dropdown.Item disabled>Loading...</Dropdown.Item>
                      ) : (
                        developers.map((dev) => (
                          <Dropdown.Item
                            key={dev._id}
                            eventKey={JSON.stringify(dev)}
                          >
                            {dev.name}
                          </Dropdown.Item>
                        ))
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={newTask.dueDate ? format(new Date(newTask.dueDate), 'yyyy-MM-dd') : ''}
                    onChange={handleInputChange}
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleAddTask} disabled={isAddingTask}>
            {isAddingTask ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaPlus className="me-2" />
            )}
            {isAddingTask ? "Adding..." : "Add Task"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCloseEditModal} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success">
            <FaEdit className="me-2" />
            Edit Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3">
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Task Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="task"
                    value={editTask.task}
                    onChange={handleEditInputChange}
                    required
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={editTask.description}
                    onChange={handleEditInputChange}
                    required
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={editTask.priority}
                    onChange={handleEditInputChange}
                    required
                    className="py-2 border-2"
                  >
                    <option value="High" className="text-danger">High</option>
                    <option value="Medium" className="text-warning">Medium</option>
                    <option value="Low" className="text-success">Low</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={editTask.status}
                    onChange={handleEditInputChange}
                    required
                    className="py-2 border-2"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Almost Done">Almost Done</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Progress (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="progress"
                    value={editTask.progress}
                    onChange={handleEditInputChange}
                    min="0"
                    max="100"
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Assigned To</Form.Label>
                  <Dropdown onSelect={(eventKey) => handleEditDeveloperSelect(JSON.parse(eventKey))}>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="dropdown-assigned-edit"
                      className="w-100 d-flex justify-content-between align-items-center"
                    >
                      {editSelectedDeveloper ? editSelectedDeveloper.name : "Select Developer"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {developerLoading ? (
                        <Dropdown.Item disabled>Loading...</Dropdown.Item>
                      ) : (
                        developers.map((dev) => (
                          <Dropdown.Item
                            key={dev._id}
                            eventKey={JSON.stringify(dev)}
                          >
                            {dev.name}
                          </Dropdown.Item>
                        ))
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={editTask.dueDate ? format(new Date(editTask.dueDate), 'yyyy-MM-dd') : ''}
                    onChange={handleEditInputChange}
                    className="py-2 border-2"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleUpdateTask} disabled={isUpdatingTask}>
            {isUpdatingTask ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaEdit className="me-2" />
            )}
            {isUpdatingTask ? "Updating..." : "Update Task"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger fw-bold">
            <FaTrash className="me-2" />
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this task? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteTask} disabled={isDeletingTask}>
            {isDeletingTask ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaTrash className="me-2" />
            )}
            {isDeletingTask ? "Deleting..." : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskTracker;